/**
 * Site Service for Arc Site Service
 * @author Christine Movius, Umer Hassan, Brendan Magee
 */

function siteService(
  $http,
  $log,
  $q,
  companyService,
  localStorageService
) {
  const service = {
    cache: {}
  };

  const endpoint = '/siteservice-readonly/api/v3';

  const setDefaultWebsite = (websites) => {
    const [firstWebsite] = websites;
    service.cache.defaultWebsite = websites.find(site => site.is_default_website === true)
      || firstWebsite;
  };

  service.getDefaultWebsite = () => {
    if (service.cache.defaultWebsite) {
      return $q.when(service.cache.defaultWebsite);
    }

    return $http.get(`${endpoint}/website/`).then((response) => {
      setDefaultWebsite(response.data);
      return service.cache.defaultWebsite;
    });
  };

  service.getHierarchies = () => {
    if (
      service.cache.hierarchies
      && service.cache.hierarchies.length > 0
    ) {
      return $q.when(service.cache.hierarchies);
    }

    return $http.get(`${endpoint}/hierarchy/`).then((response) => {
      service.cache.hierarchies = response.data;
      return response.data;
    });
  };

  service.getNavigation = (websiteId, params) => {
    if (
      service.cache.navigation
      && service.cache.navigation._website === websiteId
      && params.hierarchy === service.cache.hierarchy
    ) {
      return $q.when(service.cache.navigation);
    }

    return $http.get(`${endpoint}/navigation/${websiteId}/`, { params })
      .then((response) => {
        service.cache.navigation = response.data;
        service.cache.hierarchy = params.hierarchy;
        return response.data;
      });
  };

  service.getSectionName = (path) => {
    let sectionName = '';
    if (service.cache.sections) {
      const section = service.cache.sections.q_results.find(sec => path === sec._id);
      sectionName = section ? section.name : '';
    }
    return sectionName;
  };

  service.getSections = (id) => {
    if (service.sections && service.getSelectedWebsite()._id === id) {
      return $q.when(service.cache.sections);
    }

    return $http.get(`${endpoint}/website/${id}/section?include_inactive=false&limit=5000`).then((response) => {
      service.cache.sections = response.data;
      return response.data;
    });
  };

  service.getHierarchyByWebsite = website =>
    companyService.get()
      .then((company) => {
        const hierarchies = company.siteServiceHierarchies || [];
        const preference = hierarchies
          .find(pref => pref.website === website);
        return preference && preference.hierarchy ? preference.hierarchy : 'default';
      });

  service.getFilteredSections = id =>
    service.getHierarchyByWebsite(id)
      .then(hierarchy =>
        $http.get(`${endpoint}/website/${id}/section?include_inactive=false&limit=5000`)
          .then((response) => {
            const results = response.data && response.data.q_results ? response.data.q_results : [];

            return results
              .reduce((acc, section) => {
                if (section.parent && section.parent[hierarchy]) {
                  acc.push({
                    name: section.name,
                    path: section._id,
                    website: section._website,
                    displayName: `${section.name} (${section._id})`
                  });
                }
                return acc;
              }, []);
          }));

  service.getSectionsByPath = (path) => {
    let sections = null;
    if (service.cache.sections) {
      if (Array.isArray(path)) {
        sections = [];
        path.forEach((sectionPath) => {
          const matchingSection = service.cache.sections.q_results
            .find(section => section._id === sectionPath);
          if (matchingSection) {
            sections.push(matchingSection);
          }
        });
      } else {
        sections = service.cache.sections.q_results.filter(section => section._id === path);
      }
    }
    return sections;
  };

  service.getWebsites = () => {
    if (service.cache.websites && service.cache.websites.length) {
      return $q.when(service.cache.websites);
    }

    return $http.get(`${endpoint}/website/`).then((response) => {
      service.cache.websites = response.data;
      setDefaultWebsite(response.data);
      return response.data;
    });
  };

  service.getSelectedWebsite = () => {
    if (localStorageService.get('selectedWebsite')) {
      return localStorageService.get('selectedWebsite');
    } else if (service.cache.selectedWebsite) {
      return service.cache.selectedWebsite;
    }
    return null;
  };

  service.setSelectedWebsite = (website) => {
    if (website && website._id) {
      service.getSections(website._id);
      service.cache.selectedWebsite = website;
      localStorageService.set('selectedWebsite', website);
      return service.cache.selectedWebsite;
    }
    $log.error('website must be an object with property `_id`');
    return null;
  };

  return service;
}

const serviceArray = [
  '$http',
  '$log',
  '$q',
  'companyService',
  'localStorageService',
  siteService
];

module.exports = (ngModule) => {
  ngModule.factory('siteService', serviceArray);
};
